/**
 * Generic Kalshi ticker parsing types
 *
 * Supports multiple sports and market types with a unified interface.
 */

/** Supported sports/market categories */
export type TickerCategory =
  // NBA
  | 'nba-game' // KXNBAGAME - NBA game moneylines
  | 'nba-spread' // KXNBASPREAD - NBA spread markets
  | 'nba-total' // KXNBATOTAL - NBA totals (over/under)
  | 'nba-season' // KXNBA - NBA season props (finals, MVP, etc.)
  // WNBA
  | 'wnba-game' // KXWNBAGAME - WNBA game moneylines
  | 'wnba-spread' // KXWNBASPREAD - WNBA spread markets
  | 'wnba-total' // KXWNBATOTAL - WNBA totals (over/under)
  | 'wnba-season' // KXWNBA - WNBA season props
  // NFL
  | 'nfl-game' // KXNFLGAME - NFL game moneylines
  | 'nfl-spread' // KXNFLSPREAD - NFL spread markets
  | 'nfl-total' // KXNFLTOTAL - NFL totals
  // NFL per-game player props (nfl-player-props, 2026-09-03). Stat ladders are
  // yes-ladders like MLB's (`<event>-<TEAM><PLAYER>-<N>`, floor_strike =
  // N − 0.5); the touchdown families are structured yes/no per player with no
  // threshold segment (+ a NONE leg on FIRSTTD; DSTTD is a per-game `-Y`).
  | 'nfl-pass-yds' // KXNFLPASSYDS - passing yards
  | 'nfl-pass-td' // KXNFLPASSTDS - passing touchdowns
  | 'nfl-pass-att' // KXNFLPASSATT - pass attempts
  | 'nfl-pass-comp' // KXNFLPASSCOMP - completions
  | 'nfl-pass-int' // KXNFLPASSINT - interceptions thrown
  | 'nfl-rec-yds' // KXNFLRECYDS - receiving yards
  | 'nfl-rec' // KXNFLREC - receptions
  | 'nfl-long-rec' // KXNFLLONGREC - longest reception
  | 'nfl-rush-yds' // KXNFLRSHYDS - rushing yards
  | 'nfl-rush-att' // KXNFLRSHATT - rush attempts
  | 'nfl-long-rush' // KXNFLLONGRSH - longest rush
  | 'nfl-rush-rec-yds' // KXNFLRRYDS - rushing + receiving yards
  | 'nfl-td' // KXNFLTD - touchdowns scored (per-player yes-ladder, "1+")
  | 'nfl-2td' // KXNFL2TD - NFL 2+ touchdowns prop (binary per player)
  | 'nfl-first-td' // KXNFLFIRSTTD - NFL first touchdown prop (binary per player + NONE leg)
  | 'nfl-anytime-td' // KXNFLANYTD - anytime touchdown scorer (binary per player)
  | 'nfl-dst-td' // KXNFLDSTTD - per-game D/ST touchdown yes/no
  | 'nfl-tackles' // KXNFLTKL - tackles
  | 'nfl-sacks' // KXNFLSACK - sacks
  | 'nfl-int' // KXNFLINT - interceptions
  | 'nfl-superbowl' // KXSB - Super Bowl winner
  // NHL
  | 'nhl-game' // KXNHLGAME - NHL game moneylines
  | 'nhl-spread' // KXNHLSPREAD - NHL spread markets
  | 'nhl-total' // KXNHLTOTAL - NHL totals
  // MLB
  | 'mlb-game' // KXMLBGAME - MLB game moneylines
  | 'mlb-spread' // KXMLBSPREAD - MLB run-line spreads
  | 'mlb-total' // KXMLBTOTAL - MLB totals (over/under)
  // MLB player props — yes-ladders (`<event>-<TEAM><PLAYERCODE>-<N>` resolves
  // YES at N or more; floor_strike = N − 0.5). One category per stat series.
  | 'mlb-hits' // KXMLBHIT - player hits
  | 'mlb-hr' // KXMLBHR - player home runs
  | 'mlb-rbi' // KXMLBRBI - player RBIs
  | 'mlb-tb' // KXMLBTB - player total bases
  | 'mlb-sb' // KXMLBSB - player stolen bases
  | 'mlb-ks' // KXMLBKS - pitcher strikeouts
  | 'mlb-outs' // KXMLBOUTS - pitcher outs recorded
  | 'mlb-hrr' // KXMLBHRR - player hits + runs + RBIs
  // MLB per-game event markets. NOTE: 'mlb-teamtotal' deliberately does NOT
  // end in '-total' — describeMarket's deriveBetTeam branches on that suffix
  // for game-total strike math that doesn't apply to the per-team ladder.
  | 'mlb-teamtotal' // KXMLBTEAMTOTAL - team runs yes-ladder (`<event>-<TEAM><N>`)
  | 'mlb-rfi' // KXMLBRFI - run in the 1st inning (YRFI/NRFI; market ticker == event ticker)
  | 'mlb-extras' // KXMLBEXTRAS - game reaches extra innings (`<event>-EXTRAS`)
  // Playoff series winners (one ticker per outcome team)
  | 'nba-series' // KXNBASERIES-{YY}{TEAM1}{TEAM2}R{N}-{OUTCOME}
  | 'wnba-series' // KXWNBASERIES-{YY}{TEAM1}{TEAM2}R{N}-{OUTCOME}
  | 'nhl-series' // KXNHLSERIES-{YY}{TEAM1}{TEAM2}R{N}-{OUTCOME}
  | 'mlb-series' // KXMLBSERIES-{YY}{TEAM1}{TEAM2}R{N}-{OUTCOME}
  // College Basketball
  | 'ncaa-game' // KXNCAA*GAME - College basketball game
  | 'ncaa-spread' // KXNCAA*SPREAD - College basketball spreads
  | 'ncaa-total' // KXNCAA*TOTAL - College basketball totals
  // College Football
  | 'cfb-game' // KXNCAAFGAME - College football game moneylines
  | 'cfb-spread' // KXNCAAFSPREAD - College football spreads
  | 'cfb-total' // KXNCAAFTOTAL - College football totals
  // Tennis
  | 'tennis-match' // KXATPMATCH, KXWTAMATCH - Tennis match moneylines
  // UFC / MMA
  | 'ufc-fight' // KXUFCFIGHT - UFC fight moneylines (one ticker per fighter outcome)
  // Boxing
  | 'boxing-fight' // KXBOXING - boxing fight moneylines
  // Esports
  | 'esports-game' // KXCS2GAME, KXDOTA2GAME, KXVALORANTGAME, KXLOLGAME - esports match moneylines
  // Cricket
  | 'cricket-match' // KXWT20MATCH - cricket match moneylines
  // Soccer (3-way moneyline: Home/Away/Draw; spread/total are 2-way per line)
  | 'soccer-game' // KXUECLGAME, KXWCGAME, KXINTLFRIENDLYGAME - soccer match moneylines
  | 'soccer-spread' // KXWCSPREAD, KXUECLSPREAD, KXINTLFRIENDLYSPREAD - full-game goal spreads
  | 'soccer-total' // KXWCTOTAL, KXUECLTOTAL, KXINTLFRIENDLYTOTAL - full-game goal totals
  // soccer-goalscorer-props (2026-09-09): per-league player props served
  // under the SOCCERPROPS flow label. GOAL is a yes-ladder per player
  // (`KXEPLGOAL-26SEP06ARSCFC-ARSNMADUE20-1`, "Noni Madueke: 1+ goals");
  // FIRSTGOAL is a field of binaries (`…-CFCWFOFAN29`, plus a `NOGOAL` leg).
  | 'soccer-goal' // KX<LEAGUE>GOAL - goals scored ladder per player
  | 'soccer-first-goal' // KX<LEAGUE>FIRSTGOAL - first goalscorer (player legs + NOGOAL)
  | 'golf-future' // KXPRESCUP - golf team-event / tournament winner futures (no game, no date)
  // Other
  | 'politics' // KXATTY*, etc. - Political markets
  | 'crypto' // KXBTC*, etc. - Crypto markets
  | 'polymarket' // PM-{sport}-{away}-{home}-{date}{suffix}? or raw 0x condition id
  | 'unknown'; // Fallback for unrecognized tickers

/** Parsed ticker information */
export interface ParsedTicker {
  /** Original ticker string */
  raw: string;

  /** Detected category */
  category: TickerCategory;

  /** Series ticker (e.g., KXNBAGAME, KXNFL2TD) */
  series: string;

  /** Parsed date if available (YYYY-MM-DD format) */
  date: string | null;

  /** Game/event description if available */
  game: GameInfo | null;

  /** Player information if available (for prop bets) */
  player: PlayerInfo | null;

  /** Spread info if this is a spread market */
  spread: SpreadInfo | null;

  /** Total info if this is a total (over/under) market */
  total: TotalInfo | null;

  /** Tennis match info if this is a tennis market */
  tennis: TennisMatchInfo | null;

  /** Fight info if this is a UFC/MMA market. Reuses the two-competitor shape;
   *  player1/player2 are the two fighters. */
  fight?: TennisMatchInfo | null;

  /** Market-specific outcome (e.g., team code, strike value) */
  outcome: string | null;

  /** Human-readable short description */
  shortLabel: string;

  /** Human-readable full description */
  fullLabel: string;

  /** Set on KX{NBA|NHL|MLB}SERIES-… playoff-series-winner tickers. */
  playoffSeries?: PlayoffSeriesInfo | null;

  /** Sub-period of play for period markets — NBA 1st half + quarters, soccer
   *  1H/2H, MLB 'F5' (First 5 Innings). Absent = full game. The market otherwise
   *  parses identically to its full-game counterpart (same game / date /
   *  outcome), so period bets still roll up under the canonical game — the
   *  period only annotates the side label. */
  period?: '1H' | '2H' | 'F5' | '1Q' | '2Q' | '3Q' | '4Q';
}

/** Playoff series-winner metadata (round + 2-digit year). */
export interface PlayoffSeriesInfo {
  /** Round number (1, 2, …). */
  round: number;
  /** 2-digit year as it appears in the ticker, e.g. "26". */
  year: string;
}

export interface GameInfo {
  /** Away team code (e.g., SEA, BOS) */
  awayCode: string;
  /** Home team code (e.g., NE, MIA) */
  homeCode: string;
  /** Away team full name if known */
  awayName?: string;
  /** Home team full name if known */
  homeName?: string;
  /** Doubleheader game number from the G1/G2 team-segment suffix
   *  (`26JUL221305PITNYYG1`). Absent on single games. */
  gameNumber?: 1 | 2;
}

export interface PlayerInfo {
  /** Team code (e.g., SEA) */
  teamCode: string;
  /** Player identifier from ticker (e.g., ABARNER) */
  playerId: string;
  /** Jersey number if present */
  jerseyNumber?: number;
  /** Formatted player name if we can derive it */
  displayName?: string;
}

/** Spread market info */
export interface SpreadInfo {
  /** The favored team code */
  favoredTeam: string;
  /** The line value. Readable Polymarket slugs retain the signed value exactly;
   *  legacy Kalshi parsers may use 0 when the line comes from market data. */
  spreadValue: number;
  /** Whether this is for the favorite (cover) or underdog (miss) side */
  side: 'cover' | 'miss';
}

/** Total (over/under) market info */
export interface TotalInfo {
  /** The total points line */
  totalValue: number;
  /** Whether this is over or under */
  side: 'over' | 'under';
}

/** Tennis player info */
export interface TennisPlayerInfo {
  /** Player name/code from ticker */
  playerCode: string;
  /** Formatted player name */
  displayName: string;
}

/** Tennis match info */
export interface TennisMatchInfo {
  /** First player */
  player1: TennisPlayerInfo;
  /** Second player */
  player2: TennisPlayerInfo;
  /** Tournament code if present */
  tournament?: string;
}

/** Month abbreviation to number mapping */
export const MONTH_MAP: Record<string, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

/** Parse date from YYMONDD format (e.g., 26FEB08 -> 2026-02-08) */
export function parseDateFromTicker(dateStr: string): string | null {
  if (dateStr.length < 7) return null;

  const yy = Number(dateStr.slice(0, 2));
  const monStr = dateStr.slice(2, 5).toUpperCase();
  const dd = Number(dateStr.slice(5, 7));
  const mm = MONTH_MAP[monStr];

  if (!Number.isFinite(yy) || !Number.isFinite(dd) || !mm) return null;

  const year = 2000 + yy;
  const month = String(mm).padStart(2, '0');
  const day = String(dd).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/** Format date for display (MM-DD) */
export function formatDateShort(dateYyyyMmDd: string): string {
  const parts = dateYyyyMmDd.split('-');
  if (parts.length !== 3) return dateYyyyMmDd;
  return `${parts[1]}-${parts[2]}`;
}
